import { useCallback, useRef, useState, useEffect } from 'react';

import { ComponentColumn } from '../component.constants';
import {
  getClosestNumber,
  getNewWidths,
  getSnapWidths,
  moveToNextSnap,
} from './columns-edit.utils';
import { ARROW_LEFT, ARROW_RIGHT } from 'approot/shared/ally/keyboard.hooks';
import { DIRECTION_LEFT, DIRECTION_RIGHT } from 'webclient.constants';
import { COLUMN_ID_PREFIX, HANDLE_ID_PREFIX } from './columns.constants';

export const useResizeColumns = ({
  handleUpdateColspans,
  columns,
  columnComponentId,
  slideScaleRatio,
}: {
  handleUpdateColspans: (updatedWidths: number[]) => void;
  columns: ComponentColumn[] | null;
  columnComponentId: string;
  slideScaleRatio: number;
}) => {
  const [widths, setWidths] = useState<number[] | undefined>();

  const newWidthsToSave = useRef<number[] | undefined>(undefined);
  const currentHandleIndex = useRef<number | undefined>(undefined);
  const startingPosition = useRef<number | undefined>(undefined);
  const lastClientX = useRef<number | undefined>(undefined);
  const startingWidths = useRef<number[] | undefined>(undefined);
  const snapWidths = useRef<number[] | undefined>(undefined);

  const resize = useCallback(
    (event: PointerEvent) => {
      if (
        currentHandleIndex.current === undefined ||
        !startingPosition.current ||
        !snapWidths.current ||
        !startingWidths.current
      )
        return;

      const lastPosition = lastClientX.current || startingPosition.current;

      // mouse hasn't moved
      if (lastPosition === event.clientX) return;

      const direction =
        event.clientX < lastPosition ? DIRECTION_LEFT : DIRECTION_RIGHT;

      const updatedWidths = getNewWidths({
        direction,
        snapWidths: snapWidths.current,
        startingWidths: startingWidths.current,
        currentWidths: newWidthsToSave.current || startingWidths.current,
        currentColumnIndex: currentHandleIndex.current,
        startingPosition: startingPosition.current,
        currentPosition: event.clientX,
        slideScaleRatio,
      });

      setWidths(updatedWidths);
      newWidthsToSave.current = updatedWidths;
      lastClientX.current = event.clientX;
    },
    [slideScaleRatio]
  );

  const handleDownEvent = useCallback(
    (event: PointerEvent) => {
      const clientX = event.clientX;

      currentHandleIndex.current = Number(
        (event.target as HTMLButtonElement).id.replace(HANDLE_ID_PREFIX, '')
      );
      startingPosition.current = clientX;

      const columns = Array.from(
        document.querySelectorAll<HTMLDivElement>(
          `[id^="component-${columnComponentId}"] [id^="${COLUMN_ID_PREFIX}"]`
        )
      );
      const colWidths = columns.map(
        c => c.getBoundingClientRect().width / slideScaleRatio
      );
      startingWidths.current = colWidths;
      snapWidths.current = getSnapWidths(
        colWidths.reduce((accum, curr) => accum + curr, 0)
      );

      document.addEventListener('pointermove', resize, false);
    },
    [resize, columnComponentId, slideScaleRatio]
  );

  const handleUpEvent = useCallback(() => {
    if (
      snapWidths.current &&
      newWidthsToSave.current &&
      currentHandleIndex.current !== undefined
    ) {
      const closestSnap = getClosestNumber(
        newWidthsToSave.current[currentHandleIndex.current],
        snapWidths.current
      );

      const difference =
        newWidthsToSave.current[currentHandleIndex.current] - closestSnap;

      const newWidths = [...newWidthsToSave.current];

      newWidths[currentHandleIndex.current] = closestSnap;
      newWidths[currentHandleIndex.current + 1] =
        newWidths[currentHandleIndex.current + 1] + difference;

      setWidths(newWidths);
      handleUpdateColspans(newWidths);
    }

    currentHandleIndex.current = undefined;
    startingPosition.current = undefined;
    startingWidths.current = undefined;
    lastClientX.current = undefined;

    document.removeEventListener('pointermove', resize, false);
  }, [resize, handleUpdateColspans]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const focusedId = document.activeElement?.id || '';

      if (focusedId.startsWith(HANDLE_ID_PREFIX)) {
        const columns = Array.from(
          document.querySelectorAll<HTMLDivElement>(
            `[id^="component-${columnComponentId}"] [id^="${COLUMN_ID_PREFIX}"]`
          )
        );
        const colWidths = columns.map(
          c => c.getBoundingClientRect().width / slideScaleRatio
        );
        startingWidths.current = colWidths;
        snapWidths.current = getSnapWidths(
          colWidths.reduce((accum, curr) => accum + curr, 0)
        );

        const direction =
          e.key === ARROW_LEFT
            ? DIRECTION_LEFT
            : e.key === ARROW_RIGHT
              ? DIRECTION_RIGHT
              : undefined;
        const index = Number(focusedId.replace(HANDLE_ID_PREFIX, ''));

        if (direction) {
          const updatedWidths = moveToNextSnap({
            direction,
            widths: startingWidths.current,
            currentColumnIndex: index,
            snapWidths: snapWidths.current,
          });

          if (updatedWidths) {
            setWidths(updatedWidths);
            handleUpdateColspans(updatedWidths);
          }
        }
      }
    },
    [handleUpdateColspans, columnComponentId, slideScaleRatio]
  );

  useEffect(() => {
    const handles = document.querySelectorAll<HTMLButtonElement>(
      `[id^="component-${columnComponentId}"] [id^="${HANDLE_ID_PREFIX}"]`
    );

    for (var i = 0; i < handles.length; i++) {
      handles[i].addEventListener('pointerdown', handleDownEvent, false);
      handles[i].addEventListener('keydown', handleKeyDown, false);
    }

    document.addEventListener('pointerup', handleUpEvent, false);
    return () => {
      for (var i = 0; i < handles.length; i++) {
        handles[i].removeEventListener('pointerdown', handleDownEvent, false);
        handles[i].removeEventListener('keydown', handleKeyDown, false);
      }
      document.removeEventListener('pointerup', handleUpEvent, false);
    };
  }, [
    columns,
    columnComponentId,
    resize,
    handleUpdateColspans,
    handleDownEvent,
    handleUpEvent,
    handleKeyDown,
  ]);

  useEffect(() => {
    return () => {
      document.removeEventListener('pointerup', handleUpEvent, false);
    };
  }, [handleUpEvent]);

  return {
    widths,
    setWidths,
    newWidthsToSave,
  };
};
